// BrushCursorPreference — キャンバス上のカーソル種別 (ブラシ円 / 矢印) を
// per-user で localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (consult 2026-06-21、α フィードバック 123 さん 2026-06-20 由来):
//   - 旧版 (Java) 同様の **矢印カーソル切替オプション** が欲しいという要望。
//   - 既定は現状維持の **ブラシ円** (= BrushCursorOverlay のリングを表示し、
//     ネイティブ OS カーソルは `cursor: none` で隠す)。
//   - **矢印**選択時は `cursor: none` を解除して OS 標準カーソルを出し、リング
//     overlay は非表示にする (= 絵を一切隠さない、Java 版の懐古フック)。
//
// 設計 (aaPreference.ts / uiScalePreference.ts と同流儀):
//   - 値はカーソル種別の文字列 (`'brush-ring' | 'arrow'`) で保存。parse 失敗 /
//     不在 / storage 不可は **既定 (= ブラシ円)** に倒し、例外を throw しない。
//   - StorageLike 抽象 + sentinel probe で Safari private browsing / Quota を弾く。
//   - store factory は test で storage mock を注入可能。
//
// scope: ユーザー端末ローカル, サーバ同期なし。protocol 変更なし。Classic/Modern
//   共通 (= CanvasStage / useRoomChrome がレイアウト非依存に読む)。
//
// なぜ session-local でなく永続か (タスク指定): カーソル種別は「クライアント
//   ローカルの好み」であり毎回設定し直すのは煩わしい。penOnlyMode (palm rejection)
//   は session-local だったが、それは入力デバイスごとの一時的状態であるのに対し、
//   カーソル見た目の好みは端末を跨いで安定して欲しいので永続が自然。
//
// Tests: brushCursorPreference.test.ts (storage-injection style)。

/** キャンバス上のカーソル種別. 文字列で localStorage に保存される. */
export type BrushCursorType = 'brush-ring' | 'arrow';

/** localStorage キー: カーソル種別. */
export const BRUSH_CURSOR_TYPE_KEY = 'oekaki:brush-cursor-type';

/** 既定値 (= ブラシ円、現状維持). 何もしなければ従来通りリング表示。 */
export const DEFAULT_BRUSH_CURSOR_TYPE: BrushCursorType = 'brush-ring';

/**
 * 文字列が有効な BrushCursorType か判定する type guard. unknown / 不正値の
 * defensive narrowing 用.
 */
export const isBrushCursorType = (v: unknown): v is BrushCursorType =>
  v === 'brush-ring' || v === 'arrow';

/**
 * Minimal Storage shape this module uses (aaPreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface BrushCursorPreferenceStore {
  /** カーソル種別を読み出し. 不在 / 不正 / storage 不可 → 既定 (= ブラシ円). */
  get(): BrushCursorType;
  /** カーソル種別を保存. storage 不可なら no-op (例外を throw しない). */
  set(type: BrushCursorType): void;
  /** key を削除 (= 既定に戻す). テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * aaPreference.ts の probe を流用 (sentinel write + readback で Safari private
 * browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:brush-cursor-type__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateBrushCursorPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns the default, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readBrushCursorType = (storage: StorageLike): BrushCursorType => {
  try {
    const raw = storage.getItem(BRUSH_CURSOR_TYPE_KEY);
    return isBrushCursorType(raw) ? raw : DEFAULT_BRUSH_CURSOR_TYPE;
  } catch {
    return DEFAULT_BRUSH_CURSOR_TYPE;
  }
};

const writeBrushCursorType = (storage: StorageLike, type: BrushCursorType): void => {
  try {
    storage.setItem(BRUSH_CURSOR_TYPE_KEY, type);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `BrushCursorPreferenceStore` のインスタンスを構築. storage が unavailable な
 * 環境 (SSR / Safari private browsing 厳格モード) でも getter は既定を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createBrushCursorPreferenceStore = (
  opts: CreateBrushCursorPreferenceStoreOptions = {},
): BrushCursorPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_BRUSH_CURSOR_TYPE;
      return readBrushCursorType(storage);
    },
    set(type) {
      if (storage === null) return;
      writeBrushCursorType(storage, type);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(BRUSH_CURSOR_TYPE_KEY);
      } catch {
        // ignore
      }
    },
  };
};
